/**
 * src/modules/orders-list/virtual.ts — Faza 6.2.24 (2026-05-03) split.
 *
 * Sprint 21.B.2 (2026-05-02): virtual scroll list — DEFAULT mode (URL `?virtual=0`
 * wymusza legacy table). Renderuje WSZYSTKIE filtered orders używając VirtualList,
 * tylko widoczne wiersze są w DOM (overscan ±10) — smooth scroll przy 42K+ rows.
 *
 * Wycięte z `src/modules/orders-list.ts` (1651 LOC) — patrz
 * `.plans/audit-orders-list-split.md`.
 */

import { orderSearchGroupContextHtml } from '../orders-shared.ts';
import { chipZlecenHtml } from './chip-zlecenia.ts';

var state: any = (typeof window !== 'undefined' && window.__sbState) || (function () {
  var _p = new Proxy({}, {
    get: function (_, k: any) { return window.__sbState ? (window.__sbState as any)[k] : undefined; },
    set: function (_, k: any, v: any) { if (window.__sbState) (window.__sbState as any)[k] = v; return true; },
    has: function (_, k: any) { return window.__sbState ? (k in (window.__sbState as any)) : false; },
  });
  return _p;
})();

// Lazy delegates (cross-module)
function showOrderDetail(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.showOrderDetail) || window.showOrderDetail) as any).apply(this, args); }

// 2026-06-10 BUGFIX: order.status bywa STRINGIEM (rekord, ktory nie przeszedl przez
// normalizeBackendOrder). Bez guardu odczyt .key/.label/.color rzucal/dawal undefined →
// pusty kafelek statusu. Mirror guardu z cards.ts (od 2026-06-10).
function coerceStatusObj(status: any): { key: string; label: string; color: string } {
  if (status && typeof status === 'object') {
    return { key: status.key || '', label: status.label || '', color: status.color || 'info' };
  }
  if (status) {
    var w: any = window;
    var clrFn = (w.__sbExports && w.__sbExports.statusColorForLabel) || w.statusColorForLabel;
    var color = 'info';
    if (typeof clrFn === 'function') { try { color = clrFn(String(status)); } catch (_) {} }
    return { key: String(status).toLowerCase().replace(/\s+/g, '_'), label: String(status), color: color };
  }
  return { key: 'new', label: 'Nowe', color: 'info' };
}

// Faza L.4 — virtual scroll mode.
// Sprint 21.B.2 (2026-05-02): teraz DEFAULT (nie opt-in).
// URL `?virtual=0` wymusza legacy DOM table mode (debug/fallback gdy >50K rows).
// Renderuje WSZYSTKIE filtered orders (nie paginated) używając VirtualList.
// Tylko widoczne wiersze są w DOM (overscan ±10) — smooth scroll przy 42K+ rekordów (30 dni × 1400/dzień).
export function _isOrdersVirtualMode() {
  try {
    var qs = new URLSearchParams(window.location.search);
    if (qs.get('virtual') === '0') return false;  // explicit opt-out
    return true;  // Sprint 21.B.2: default ON
  } catch (_: any) { return true; }
}

// 2026-05-25: _ordersVirtualList variable usunięta — refaktor wywalił VirtualList
// (server already paginates max 50 rows = direct map render wystarczy).

export function _renderOrdersVirtualList(filtered: any) {
  var listContainer = document.getElementById('orders-list-container');
  if (!listContainer) return;
  // ETAP 4: JEDEN listener delegowany na kontenerze (nie po jednym na wierszu) —
  // lista przerysowuje sie przy kazdym filtrze, wiec listenery per wiersz trzeba by
  // wiazac od nowa i predzej czy pozniej ktorys render by o tym zapomnial.
  try { (window as any).__sbOrderTags && (window as any).__sbOrderTags.uzbrojListeNaUpuszczanie(listContainer); } catch (_e) { /* lista dziala dalej */ }

  // 2026-05-25: REFAKTOR — wywalony VirtualList (server już paginuje per_page=50 = max 50 rows),
  // brak fixed-height/scroll container, brak ramki. Lista płynie naturalnie w stronie jak w
  // widoku kart. Paginacja widoczna na dole (renderPagination z `state.itemsPerPage`).
  var selectMode = !!(state as any).ordersSelectMode;
  // 2026-05-25: layout — produkty zajmują WSZYSTKO co zostaje, fixed kolumny ścisłe.
  // Status 90px (tight fit "Anulowane"=72px), Źródło 130px (Allegro+konto), Kraj 44px,
  // Kwota 110px (np. 3 390,00 HUF), Data 130px (25.05.2026 15:50).
  // 2026-07-19 (audyt RWD #6): kolumny przeniesione z inline do klasy .orders-vrow (+.select-mode)
  // w style.css — na ≤768px Produkty dostają minmax(140px,1fr), a tabela scrolluje w .orders-vtable.
  var rowClass = 'orders-vrow' + (selectMode ? ' select-mode' : '');

  // 2026-05-25: helpers wzorowane na dashboardzie. Thumbnail produktu (Allegro offer image)
  // dla wizualnego odróżnienia ofert — fallback do sellbase_image_url gdy brak offer.
  function _fmtItems(order: any): string {
    var items = Array.isArray(order.items) ? order.items : [];
    if (!items.length) {
      var n = parseInt(order.productCount || 0, 10);
      return n > 0 ? '<span style="font-size:12px">' + n + ' szt.</span>' :
        '<span style="color:var(--color-text-muted);font-size:11px">—</span>';
    }
    var first = items[0];
    var name = String((first && (first.name || first.product_name || first.title)) || '—');
    var qty = parseInt((first && first.quantity) || 1, 10) || 1;
    var imgUrl = String((first && (first.offer_image_url || first.sellbase_image_url || first.image_url)) || '');
    var thumb = imgUrl
      ? '<img class="sb-thumb sb-zoom" src="' + escapeAttr(imgUrl) + '" alt="" loading="lazy" style="width:32px;height:32px;border-radius:4px;object-fit:cover;flex-shrink:0;border:1px solid var(--color-border)">'
      : '<div style="width:32px;height:32px;border-radius:4px;background:var(--color-surface-2);border:1px solid var(--color-border);display:inline-flex;align-items:center;justify-content:center;flex-shrink:0"><i data-lucide="package" style="width:14px;height:14px;opacity:.4"></i></div>';
    // 2026-05-25: badge ×N TYLKO gdy qty > 1 (×1 nadmiarowe — qty 1 to default zamówienia).
    var qtyBadge = qty > 1
      ? '<span style="display:inline-block;background:var(--color-primary-highlight);color:var(--color-primary);font-size:10px;font-weight:700;padding:1px 7px;border-radius:9999px;flex-shrink:0;font-variant-numeric:tabular-nums">×' + qty + '</span>'
      : '';
    var extra = items.length - 1;
    var moreBadge = extra > 0
      ? '<span style="display:inline-block;background:var(--color-surface-2);color:var(--color-text-muted);font-size:10px;padding:1px 6px;border-radius:9999px;flex-shrink:0">+' + extra + ' więcej</span>'
      : '';
    // 2026-05-25: badge'y bezpośrednio po tytule (nie rozciągnięte do końca grid'a).
    // Tytuł: flex 0 1 auto — naturalna długość, skraca się gdy braknie miejsca dla badge'ów.
    // Badge'y: flex-shrink:0, gap:6px — sklejone z końcem tytułu.
    var h =
      '<div style="display:flex;align-items:center;gap:8px;min-width:0">' +
        thumb +
        '<div style="display:flex;align-items:center;gap:6px;min-width:0;flex:1">' +
          '<span title="' + escapeAttr(name) + '" style="font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;flex:0 1 auto">' + escapeHtml(name) + '</span>' +
          qtyBadge +
          moreBadge +
        '</div>' +
      '</div>';
    return h;
  }
  function _fmtSource(order: any): string {
    // 2026-05-25: bez kropki przed (no-dot) i bez `·` separatora — gładki ciąg "Allegro HurtowniaXXL"
    var srcLabel = (order.source && order.source.label) || (order.source && order.source.key) || 'Ręczne';
    var srcColor = (order.source && order.source.color) || '';
    var acc = order.allegroAccountName || order._account_name || order.accountName || '';
    var inner = escapeHtml(srcLabel);
    if (acc) inner += ' <span style="opacity:.75;font-size:10px">' + escapeHtml(acc) + '</span>';
    return '<span class="status-badge no-dot ' + srcColor + '" style="font-size:10px;white-space:nowrap">' + inner + '</span>';
  }
  // 2026-05-25: kraj kupującego — flaga (flagcdn.com 16x12). Country code z buyer_country
  // lub delivery address. Format wynikowy: "🇵🇱 PL" (flaga + ISO code small).
  function _fmtCountry(order: any): string {
    var addr = (order.delivery && order.delivery.address) || order.shippingAddress || {};
    var code = String(
      addr.countryCode || addr.country_code || addr.country ||
      order.buyer_country || 'PL'
    ).toUpperCase().slice(0, 2);
    if (!code || code.length !== 2) return '<span style="font-size:10px;color:var(--color-text-faint)">—</span>';
    return '<span title="' + escapeAttr(code) + '" style="display:inline-flex;align-items:center;gap:4px;font-size:10px;color:var(--color-text-muted)">' +
      '<img src="https://flagcdn.com/20x15/' + code.toLowerCase() + '.png" alt="' + escapeAttr(code) + '" loading="lazy" style="width:20px;height:15px;border-radius:2px;display:block;background:var(--color-surface-2)">' +
    '</span>';
  }
  function _fmtDateTime(order: any): string {
    var raw = order._sortDate || order.date || order.bought_at || order.dateFormatted || '';
    if (!raw) return escapeHtml(String(order.dateFormatted || ''));
    try {
      var d = new Date(raw);
      if (isNaN(d.getTime())) return escapeHtml(String(order.dateFormatted || ''));
      var pad = function(n: number) { return String(n).padStart(2, '0'); };
      return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear() +
        ' <span style="opacity:.7">' + pad(d.getHours()) + ':' + pad(d.getMinutes()) + '</span>';
    } catch (_) {
      return escapeHtml(String(order.dateFormatted || ''));
    }
  }

  // Render wszystkich wierszy (max 50 z server pagination — bez VirtualList overhead)
  var i18nMod = window.__sbI18n;
  var rowsHtml = '';
  filtered.forEach(function(order: any) {
    var isChecked = (state as any).ordersSelectedIds.has(order.id) ? ' checked' : '';
    var statusObj = coerceStatusObj(order.status);
    // ETAP 3: nazwa nadana przez firmę bije tłumaczenie (patrz `etykietaStatusu`).
    var _etykieta = (window as any).__sbExports && (window as any).__sbExports.etykietaStatusu;
    var statusLabel = _etykieta
      ? _etykieta(statusObj.key, statusObj.label)
      : (i18nMod && i18nMod.tStatus ? i18nMod.tStatus('order', statusObj.key, statusObj.label) : statusObj.label);
    var checkCol = selectMode
      ? '<span class="orders-vrow-check-col" onclick="event.stopPropagation()"><input type="checkbox" class="order-row-check" data-order-id="' + escapeAttr(order.id) + '"' + isChecked + '></span>'
      : '';
    rowsHtml += orderSearchGroupContextHtml(order) +
      '<div class="order-row ' + rowClass + '" data-order-id="' + escapeAttr(order.id) + '" style="align-items:center;padding:8px 12px;border-bottom:1px solid var(--color-border);cursor:pointer;font-size:13px">' +
        checkCol +
        // ETAP 4: chipy tagow przy produktach. To jest wiersz, ktory user naprawde
        // widzi w widoku „tabela" — `table.ts` renderuje <tr>, ktorego nie widac
        // poza `?virtual=0`.
        '<div style="min-width:0">' + _fmtItems(order) +
          (((window as any).__sbOrderTags) ? (window as any).__sbOrderTags.chipyTagow(order.tags) : '') +
          (((window as any).__sbOrderTags && (window as any).__sbOrderTags.chipCzarnejListy) ? (window as any).__sbOrderTags.chipCzarnejListy(order) : '') +
          (((window as any).__sbOrderTags && (window as any).__sbOrderTags.chipProdukcji && (order as any).production)
            ? (window as any).__sbOrderTags.chipProdukcji((order as any).production, order) : '') +
          chipZlecenHtml((order as any).zlecenia) +
        '</div>' +
        '<div><span class="status-badge no-dot ' + statusObj.color + '" data-status-cat="order" data-status-key="' + escapeAttr(statusObj.key || '') + '" style="font-size:10px">' + escapeHtml(statusLabel) + '</span></div>' +
        '<div style="overflow:hidden">' + _fmtSource(order) + '</div>' +
        '<div>' + _fmtCountry(order) + '</div>' +
        '<div style="font-weight:600;font-variant-numeric:tabular-nums">' + SellBaseData.formatCurrency(order.amount, order.currency) + '</div>' +
        '<div style="font-size:11px;font-variant-numeric:tabular-nums;white-space:nowrap">' + _fmtDateTime(order) + '</div>' +
      '</div>';
  });

  // 2026-05-25: nowy markup — bez ramki, bez scroll containera, lista płynie w stronie.
  // Header sticky w obrębie kontenera widoku (nie tylko host).
  listContainer.innerHTML =
    '<div class="orders-vtable">' +
    '<div class="orders-virtual-header ' + rowClass + '" data-select-mode="' + selectMode + '" style="padding:10px 12px;font-size:11px;font-weight:600;color:var(--color-text-muted);border-bottom:1px solid var(--color-border);background:var(--color-surface-2);text-transform:uppercase;letter-spacing:.04em">' +
      (selectMode ? '<span></span>' : '') +
      '<span>Produkty</span><span>Status</span><span>Źródło</span><span>Kraj</span><span>Kwota</span><span>Data</span>' +
    '</div>' +
    '<div class="orders-virtual-host">' + (rowsHtml || '<div style="padding:32px;text-align:center;color:var(--color-text-muted)">Brak zamówień spełniających kryteria.</div>') + '</div>' +
    '</div>';

  // Lucide icons (np. package fallback ikon przy items bez thumbnail)
  try {
    if (typeof window.lucide !== 'undefined') window.lucide.createIcons({ nodes: [listContainer as HTMLElement] });
  } catch (_: any) {}

  // 2026-05-25: Delegated click — klik wiersza otwiera drawer (pomija checkbox col).
  var host = listContainer.querySelector('.orders-virtual-host') as HTMLElement | null;
  if (host) {
    host.addEventListener('click', function(this: any, e: any) {
      var t = e.target as HTMLElement;
      if (t.closest('.orders-vrow-check-col')) return;
      var row = t.closest('.order-row');
      if (!row) return;
      showOrderDetail((row as any).dataset.orderId);
    });
    host.addEventListener('change', function(this: any, e: any) {
      var t = e.target as HTMLInputElement;
      if (!t.classList.contains('order-row-check')) return;
      var oid = t.dataset.orderId;
      if (!oid) return;
      if (t.checked) (state as any).ordersSelectedIds.add(oid);
      else (state as any).ordersSelectedIds.delete(oid);
      if (typeof (window as any)._updateBulkBar === 'function') (window as any)._updateBulkBar();
    });
  }

  // 2026-05-25: pokazuj paginację (jak w widoku kart) — wcześniej była ukryta dla virtual list.
  var pager = document.getElementById('orders-pagination');
  if (pager) pager.style.display = '';
  var totalItems = (state as any).ordersTotalCount != null ? (state as any).ordersTotalCount : filtered.length;
  var perPage = (state as any).itemsPerPage || 50;
  var totalPages = (state as any).ordersTotalPages || Math.ceil(totalItems / perPage) || 1;
  var page = ((state as any).currentPage && (state as any).currentPage.orders) || 1;
  if (page > totalPages && totalPages > 0) page = totalPages;
  try {
    if (typeof (window as any).renderPagination === 'function') {
      (window as any).renderPagination('orders-pagination', page, totalPages, totalItems, 'orders');
    }
  } catch (_: any) {}
}

// ── EXPOSE ──
(window as any).SellBaseApp = (window as any).SellBaseApp || {};
(window as any)._isOrdersVirtualMode = _isOrdersVirtualMode; (window as any).SellBaseApp._isOrdersVirtualMode = _isOrdersVirtualMode;
(window as any)._renderOrdersVirtualList = _renderOrdersVirtualList; (window as any).SellBaseApp._renderOrdersVirtualList = _renderOrdersVirtualList;
